import React, { useState } from "react";
import { Plus, Minus, MapPin, HelpCircle } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import FinalCTA from "@/sections/FinalCTA";
import { FAQ_CATEGORIES, ALL_FAQS } from "@/lib/faqs-data";

const faqPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: ALL_FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export default function FaqsPage() {
  const [active, setActive] = useState("local");
  // Multiple open allowed; keys are "catId-index"
  const [open, setOpen] = useState({ "local-0": true });

  const activeGroup = FAQ_CATEGORIES.find((g) => g.id === active) || FAQ_CATEGORIES[0];

  const toggle = (id) => setOpen((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div data-testid="page-faqs">
      <Seo
        title="Company Registration FAQs for Mumbai Founders"
        description="Answers on Pvt Ltd, LLP, OPC and GST registration for founders in Mumbai, Navi Mumbai, Thane and the MMRDA region."
        path="/faqs"
        jsonLd={faqPageSchema()}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "FAQs", path: "/faqs" },
        ]}
      />

      {/* Hero */}
      <section className="bg-white border-b border-[#EAEAEA]">
        <div className="max-w-[1024px] mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
              <HelpCircle size={14} className="text-[#FDD805]" /> FAQs
            </div>
            <h1 className="mt-4 font-display text-[38px] md:text-[56px] leading-[1.02] font-black tracking-tight text-[#0F1115]">
              Frequently asked{" "}
              <span className="hl-yellow">questions</span>.
            </h1>
            <p className="mt-5 text-[16px] md:text-[18px] text-[#1C1F26] max-w-2xl mx-auto leading-relaxed">
              Company, LLP, OPC and GST registration answers, with dedicated
              guidance for founders in Mumbai, Navi Mumbai, Thane and the wider
              MMRDA region.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Category tabs */}
      <section className="bg-white border-b border-[#EAEAEA] sticky top-[88px] z-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-5">
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="faq-category-tabs">
            {FAQ_CATEGORIES.map((g) => {
              const isActive = active === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => setActive(g.id)}
                  className={`shrink-0 px-4 py-2.5 rounded-full text-[13px] font-bold border-2 transition-colors ${isActive
                      ? "bg-[#0F1115] text-white border-[#0F1115]"
                      : "bg-white text-[#0F1115] border-[#EAEAEA] hover:border-[#0F1115]"
                    }`}
                  data-testid={`faq-tab-${g.id}`}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Render ALL categories in DOM (SEO-friendly) but hide inactive ones */}
      <section className="bg-white">
        <div className="max-w-[900px] mx-auto px-5 md:px-8 py-14 md:py-20">
          {FAQ_CATEGORIES.map((g) => (
            <div
              key={g.id}
              className={g.id === active ? "" : "hidden"}
              aria-hidden={g.id !== active}
              data-testid={`faq-panel-${g.id}`}
            >
              <h2 className="font-display text-[24px] md:text-[32px] font-black text-[#0F1115]">
                {g.label}
              </h2>
              <p className="mt-2 text-[13px] uppercase tracking-[0.18em] font-bold text-[#4A4A4A]">
                {g.items.length} question{g.items.length > 1 ? "s" : ""}
              </p>

              <div className="mt-8 border-t border-[#EAEAEA]">
                {g.items.map((item, i) => {
                  const id = `${g.id}-${i}`;
                  const isOpen = !!open[id];
                  return (
                    <article
                      key={id}
                      className="border-b border-[#EAEAEA]"
                      data-testid={`faq-item-${id}`}
                    >
                      <button
                        onClick={() => toggle(id)}
                        className="w-full flex items-start justify-between gap-5 py-6 text-left group"
                        aria-expanded={isOpen}
                        data-testid={`faq-toggle-${id}`}
                      >
                        <div className="flex-1">
                          {item.tag && (
                            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#0F1115] bg-[#FEF6C7] px-2.5 py-1 rounded-full mb-3">
                              <MapPin size={11} className="text-[#0F1115]" />
                              {item.tag}
                            </div>
                          )}
                          <h3 className="font-display font-bold text-[16.5px] md:text-[18px] text-[#0F1115] leading-snug group-hover:underline decoration-[#FDD805] decoration-2 underline-offset-4">
                            {item.q}
                          </h3>
                        </div>
                        <span
                          className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${isOpen ? "bg-[#0F1115] text-[#FDD805]" : "bg-[#FFF9E6] text-[#0F1115]"}`}
                        >
                          {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                        </span>
                      </button>
                      <div
                        className={`overflow-hidden transition-[max-height,opacity] duration-300 ${isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}
                      >
                        <p className="pb-6 pr-14 text-[15.5px] leading-[1.7] text-[#1C1F26]">
                          {item.a}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mt-14 p-6 rounded-2xl border border-[#EAEAEA] bg-[#F8F9FA] text-[13px] text-[#4A4A4A] leading-relaxed">
            Answers reflect the regulatory framework as commonly understood.
            Rules and thresholds are set by the MCA, GST Council and Government
            of Maharashtra, and may change - reach out for current specifics via
            WhatsApp.
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
